import { MapaPreview } from '@/shared/ui/MapaPreview/MapaPreview'
import { ModalMapaUbicacionProyecto } from '@/modules/plan-proyectos/ModalMapaUbicacionProyecto'
import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase/cliente'
import type { OrdenServicioConRelaciones } from './tipos'
import { useOpcionesColaboradores } from '@/shared/datos/useOpciones'
import {
  IconoProyecto,
  IconoEditar,
  IconoMapa,
  IconoHerramienta,
  IconoCaja,
  IconoCheck,
  IconoReloj,
} from '@/shared/ui/Iconos/Iconos'
import estilos from './DetalleOrdenProyecto.module.css'

function formatearMoneda(monto: number | null | undefined) {
  if (monto == null || isNaN(monto)) return '$ 0'
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
  }).format(monto)
}

function iniciales(nombre: string) {
  return nombre
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join('')
}

type Props = {
  orden: OrdenServicioConRelaciones
}

export function DetalleOrdenProyecto({ orden }: Props) {
  const navegar = useNavigate()
  const modulo = orden.plan_modulo
  const proyecto = modulo?.etapa?.proyecto || orden.proyecto_plan

  const { opciones: opcionesColab, cargando: cargandoOpciones } = useOpcionesColaboradores()

  const [colaboradores, setColaboradores] = useState(() => {
    return (modulo?.colaboradores || []).map((c: any) => ({
      id: c.colaborador_id || c.colaborador?.id,
      nombre: c.colaborador?.nombre || 'Técnico',
      avance_porcentaje: Number(c.avance_porcentaje) || 0,
      completado: Boolean(c.completado),
      horas_trabajadas: Number(c.horas_trabajadas) || 0,
    }))
  })

  const [avanceGlobal, setAvanceGlobal] = useState(modulo?.avance ?? 0)
  const [modalCuadrillaAbierto, setModalCuadrillaAbierto] = useState(false)
  const [cuadrillaTemp, setCuadrillaTemp] = useState<string[]>([])
  const [guardandoCuadrilla, setGuardandoCuadrilla] = useState(false)
  const [toastMensaje, setToastMensaje] = useState<string | null>(null)

  function mostrarAviso(msg: string) {
    setToastMensaje(msg)
    setTimeout(() => setToastMensaje(null), 3500)
  }

  function abrirModalCuadrilla() {
    setCuadrillaTemp(colaboradores.map((c) => c.id))
    setModalCuadrillaAbierto(true)
  }

  function alternarColabTemp(id: string) {
    setCuadrillaTemp((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    )
  }

  async function handleCambiarAvanceColaborador(colaboradorId: string, nuevoAvance: number) {
    const actualizados = colaboradores.map((c) => {
      if (c.id === colaboradorId) {
        return {
          ...c,
          avance_porcentaje: nuevoAvance,
          completado: nuevoAvance >= 100,
        }
      }
      return c
    })
    setColaboradores(actualizados)

    const suma = actualizados.reduce((acc, curr) => acc + curr.avance_porcentaje, 0)
    const nuevoGlobal = actualizados.length > 0 ? Math.round(suma / actualizados.length) : nuevoAvance
    setAvanceGlobal(nuevoGlobal)

    try {
      const sb: any = supabase
      await sb.rpc('fn_actualizar_avance_colaborador', {
        p_modulo_id: modulo?.id || orden.plan_modulo_id,
        p_colaborador_id: colaboradorId,
        p_avance: nuevoAvance,
        p_completado: nuevoAvance >= 100,
      })
      mostrarAviso('Avance actualizado correctamente')
    } catch (err) {
      console.error('Error al actualizar avance:', err)
    }
  }

  async function handleConfirmarCuadrilla() {
    setGuardandoCuadrilla(true)
    try {
      const sb: any = supabase
      await sb.rpc('fn_asignar_colaboradores_modulo', {
        p_modulo_id: modulo?.id || orden.plan_modulo_id,
        p_colaboradores_ids: cuadrillaTemp,
      })

      const mapaExistentes = new Map(colaboradores.map((c) => [c.id, c]))
      const mapaNombres = new Map(opcionesColab.map((o) => [o.valor, o.etiqueta]))

      const actualizados = cuadrillaTemp.map((id) => {
        if (mapaExistentes.has(id)) return mapaExistentes.get(id)!
        return {
          id,
          nombre: mapaNombres.get(id) || 'Técnico asignado',
          avance_porcentaje: 0,
          completado: false,
          horas_trabajadas: 0,
        }
      })

      setColaboradores(actualizados)
      setModalCuadrillaAbierto(false)
      mostrarAviso('Cuadrilla asignada correctamente')
    } catch (err) {
      console.error('Error al reasignar colaboradores:', err)
      mostrarAviso('Error al guardar cuadrilla')
    } finally {
      setGuardandoCuadrilla(false)
    }
  }

  const codigoProyecto = proyecto?.codigo || 'PROYECTO'
  const nombreProyecto = proyecto?.nombre || orden.descripcion || 'Plan de Proyecto'
  const nombreEtapa = modulo?.etapa?.nombre || 'Etapa de Ejecución'
  const codigoModulo = modulo?.codigo || '1.1'
  const nombreModulo = modulo?.nombre || orden.servicio?.nombre || 'Actividad de Proyecto'

  const horasEstimadas = Number(modulo?.horas) || Number(orden.orden_trabajo?.horas_asignadas) || 0
  const horasEjecutadas = Number(orden.orden_trabajo?.horas_ejecutadas) || 0
  const presupuestoModulo = Number(orden.cotizacion?.valor_total) || 0

  const [modalMapaAbierto, setModalMapaAbierto] = useState(false)
  const [coordsLocales, setCoordsLocales] = useState<{ lat: number | null; lng: number | null; radio: number; dir: string } | null>(null)

  const lat = coordsLocales ? coordsLocales.lat : (Number(orden.latitud ?? proyecto?.latitud) || null)
  const lng = coordsLocales ? coordsLocales.lng : (Number(orden.longitud ?? proyecto?.longitud) || null)
  const geocelda = coordsLocales ? coordsLocales.radio : (Number(orden.radio_geocerca_m ?? proyecto?.radio_geocerca_m ?? 80))
  const direccionObra = coordsLocales ? coordsLocales.dir : (orden.direccion || proyecto?.ubicacion || 'Sin dirección registrada')

  const totalCompletados = colaboradores.filter((c) => c.completado || c.avance_porcentaje >= 100).length

  const elementos = Array.isArray(modulo?.elementos) ? (modulo.elementos as any[]) : []
  const materiales = elementos.filter((e) => e.tipo === 'material' || !e.tipo)
  const herramientas = elementos.filter((e) => e.tipo === 'herramienta')
  const alistamientoEstado = modulo?.alistamiento_estado || 'sin_solicitar'

  return (
    <div className={estilos.contenedor}>
      {/* ── Barra de Navegación ── */}
      <nav className={estilos.barraNavegacion} aria-label="Navegación de orden">
        <div className={estilos.migas}>
          <button
            type="button"
            className={estilos.enlaceVolver}
            onClick={() => navegar('/ordenes')}
            title="Volver a la Matriz de órdenes"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            <span>Matriz de órdenes</span>
          </button>
          <span>/</span>
          <span>{codigoProyecto}</span>
          <span>/</span>
          <span>{orden.orden_trabajo?.numero || orden.numero}</span>
        </div>

        <Link
          to="/plan-proyectos"
          className={estilos.btnIrAlPlan}
          title="Ver este proyecto en el Plan Maestro y Diagrama Gantt"
        >
          <IconoProyecto />
          <span>Abrir en Plan de Proyectos</span>
        </Link>
      </nav>

      {/* ── Tarjeta Cabecera Hero ── */}
      <header className={estilos.tarjetaHero}>
        <div className={estilos.heroFilaSuperior}>
          <div className={estilos.heroTitulos}>
            <div className={estilos.badgesProyecto}>
              <span className={estilos.badgePryCodigo}>{codigoProyecto}</span>
              <span className={estilos.badgePryNombre}>{nombreProyecto}</span>
              <span className={estilos.badgeEtapa}>{nombreEtapa}</span>
            </div>
            <h1 className={estilos.moduloTitulo}>
              <span className={estilos.moduloCodigoPill}>{codigoModulo}</span>
              {nombreModulo}
            </h1>
          </div>

          <div className={estilos.heroNumerosOrden}>
            <span className={estilos.otNumeroBadge}>
              {orden.orden_trabajo?.numero || orden.numero}
            </span>
            <span className={estilos.osNumeroSub}>Orden de Servicio: {orden.numero}</span>
          </div>
        </div>

        {/* Barra de Progreso Global */}
        <div className={estilos.barraGlobalWrap}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#e5d8ff' }}>
            <span>Avance consolidado del módulo</span>
            <b style={{ color: '#ffc43f', fontWeight: 800 }}>{avanceGlobal}%</b>
          </div>
          <div className={estilos.barraGlobalPista}>
            <div
              className={estilos.barraGlobalProgreso}
              style={{ width: Math.min(100, Math.max(0, avanceGlobal)) + '%' }}
            />
          </div>
        </div>

        {/* Métricas y KPIs */}
        <div className={estilos.kpisGrid}>
          <div className={estilos.kpiCol}>
            <span className={estilos.kpiEtiqueta}>Estado en Obra</span>
            <b className={estilos.kpiValor} style={{ color: avanceGlobal >= 100 ? '#10b981' : '#ffc43f' }}>
              {avanceGlobal >= 100 ? 'Completado' : 'En Ejecución'}
            </b>
            <span className={estilos.kpiSub}>
              {totalCompletados} de {colaboradores.length} técnicos finalizaron
            </span>
          </div>

          <div className={estilos.kpiCol}>
            <span className={estilos.kpiEtiqueta}>Presupuesto Módulo</span>
            <b className={estilos.kpiValor}>{formatearMoneda(presupuestoModulo)}</b>
            <span className={estilos.kpiSub}>Costo asignado en plan maestro</span>
          </div>

          <div className={estilos.kpiCol}>
            <span className={estilos.kpiEtiqueta}>Horas de Labor</span>
            <b className={estilos.kpiValor}>{horasEstimadas} h est.</b>
            <span className={estilos.kpiSub}>{horasEjecutadas} h ejecutadas registradas</span>
          </div>

          <div className={estilos.kpiCol}>
            <span className={estilos.kpiEtiqueta}>Cliente / Obra</span>
            <b className={estilos.kpiValor}>{orden.cliente?.nombre || 'Cliente del Proyecto'}</b>
            <span className={estilos.kpiSub}>{orden.direccion || 'Ubicación en sitio'}</span>
          </div>
        </div>
      </header>

      {/* ── Grilla Principal Dos Columnas ── */}
      <div className={estilos.grillaDosColumnas}>
        {/* Columna Izquierda: Cuadrilla y Avances */}
        <section className={estilos.seccionCard}>
          <div className={estilos.seccionHeader}>
            <div>
              <h2 className={estilos.seccionTitulo}>Cuadrilla Asignada al Módulo</h2>
              <p className={estilos.seccionSubtitulo}>
                {colaboradores.length} técnicos asignados · Ajuste individual de avance
              </p>
            </div>
            <button
              type="button"
              className={estilos.btnIrAlPlan}
              onClick={abrirModalCuadrilla}
              title="Modificar colaboradores asignados"
            >
              <IconoEditar />
              <span>Modificar Cuadrilla</span>
            </button>
          </div>

          <div className={estilos.listaColaboradores}>
            {colaboradores.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px', color: '#e5d8ff' }}>
                <p>No hay técnicos asignados a este módulo todavía.</p>
                <button
                  type="button"
                  className={estilos.btnIrAlPlan}
                  onClick={abrirModalCuadrilla}
                  style={{ marginTop: '8px' }}
                >
                  Asignar técnicos ahora
                </button>
              </div>
            ) : (
              colaboradores.map((colab) => (
                <div key={colab.id} className={estilos.tarjetaColaborador}>
                  <div className={estilos.colaboradorFilaTop}>
                    <div className={estilos.colaboradorPerfil}>
                      <span className={estilos.avatar}>{iniciales(colab.nombre)}</span>
                      <span className={estilos.nombreColaborador}>{colab.nombre}</span>
                    </div>
                    <span
                      className={
                        colab.completado || colab.avance_porcentaje >= 100
                          ? estilos.badgeCompletado
                          : estilos.badgeEnProgreso
                      }
                    >
                      {colab.completado || colab.avance_porcentaje >= 100
                        ? 'Completado'
                        : 'En ejecución'}
                    </span>
                  </div>

                  <div className={estilos.colaboradorSliderRow}>
                    <label style={{ fontSize: '0.8rem', color: '#e5d8ff', fontWeight: 600 }}>Avance:</label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="5"
                      value={colab.avance_porcentaje}
                      className={estilos.sliderAvanceIndiv}
                      title={"Ajustar porcentaje de " + colab.nombre}
                      onChange={(e) =>
                        handleCambiarAvanceColaborador(colab.id, Number(e.target.value))
                      }
                    />
                    <span className={estilos.porcentajeLectura}>{colab.avance_porcentaje}%</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

        {/* Columna Derecha: Ubicación y Geocelda */}
        <section className={estilos.seccionCard}>
          <div className={estilos.seccionHeader}>
            <div>
              <h2 className={estilos.seccionTitulo}>Ubicación y Geocelda</h2>
              <p className={estilos.seccionSubtitulo}>Parámetros de geolocalización para campo</p>
            </div>
            <button
              type="button"
              className={estilos.btnEditarUbicacion}
              onClick={() => setModalMapaAbierto(true)}
              title="Ajustar coordenadas GPS y geocelda"
            >
              <IconoMapa />
              <span>{lat != null && lng != null ? 'Modificar mapa' : 'Asignar mapa'}</span>
            </button>
          </div>

          <div className={estilos.ubicacionInfoBloque}>
            <div className={estilos.datoFila}>
              <span className={estilos.datoRotulo}>Dirección de la obra</span>
              <span className={estilos.datoTexto}>{direccionObra}</span>
            </div>

            <div className={estilos.geoceldaBadgeRow}>
              <span>Radio Geocelda:</span>
              <b style={{ color: '#ffc43f' }}>{geocelda} metros</b>
              <span style={{ fontSize: '0.78rem', color: '#e5d8ff' }}>
                (Margen para sello móvil)
              </span>
            </div>

            {lat != null && lng != null ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <MapaPreview
                  lat={lat}
                  lng={lng}
                  radioM={geocelda}
                  direccion={direccionObra}
                  alto="220px"
                  zoom={16}
                />
                <div className={estilos.datoFila}>
                  <span className={estilos.datoRotulo}>Coordenadas GPS</span>
                  <span className={estilos.datoTexto} style={{ fontFamily: 'monospace' }}>
                    {lat.toFixed(6)}, {lng.toFixed(6)}
                  </span>
                  <a
                    href={"https://www.google.com/maps/dir/?api=1&destination=" + lat + "," + lng}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      fontSize: '0.85rem',
                      color: '#ffc43f',
                      fontWeight: 800,
                      textDecoration: 'underline',
                      marginTop: '4px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    Abrir en Google Maps / Navegación exterior ↗
                  </a>
                </div>
              </div>
            ) : (
              <div style={{ background: '#2B1153', padding: '16px', borderRadius: '8px', border: '1px dashed #7339ce', textAlign: 'center' }}>
                <p style={{ fontSize: '0.88rem', color: '#e5d8ff', margin: '0 0 10px' }}>
                  Este proyecto no tiene coordenadas GPS fijadas.
                </p>
                <button
                  type="button"
                  onClick={() => setModalMapaAbierto(true)}
                  style={{
                    background: '#ffc43f',
                    color: '#1a0033',
                    border: 'none',
                    fontWeight: 800,
                    padding: '8px 16px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px',
                  }}
                >
                  📍 Asignar ubicación GPS ahora
                </button>
              </div>
            )}
          </div>
        </section>
      </div>

      {/* ── Sección Insumos y Herramientas Alistadas en Obra ── */}
      <section className={estilos.seccionInsumosCard}>
        <div className={estilos.seccionHeader}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <h2 className={estilos.seccionTitulo}>Insumos y Herramientas de la Cuadrilla</h2>
              {alistamientoEstado === 'alistado' ? (
                <span className={estilos.badgeAlistadoBodega}>
                  <IconoCheck /> Despachado / Alistado por Bodega
                </span>
              ) : alistamientoEstado === 'en_solicitud' ? (
                <span className={estilos.badgeSolicitadoBodega}>
                  <IconoReloj /> En alistamiento de Bodega
                </span>
              ) : (
                <span className={estilos.badgeSinAlistar}>
                  Insumos sin solicitud a Bodega
                </span>
              )}
            </div>
            <p className={estilos.seccionSubtitulo}>
              Equipos físicos, consumibles y herramientas a cargo del personal en campo para este módulo
            </p>
          </div>
        </div>

        {elementos.length === 0 ? (
          <div style={{ background: '#250d4a', padding: '20px', borderRadius: '8px', textAlign: 'center', color: '#cfbdf7' }}>
            <p style={{ margin: 0 }}>Este módulo no tiene insumos ni herramientas registradas en el plan de obra.</p>
          </div>
        ) : (
          <div className={estilos.grillaInsumosObra}>
            {/* Columna Materiales */}
            <div className={estilos.columnaInsumos}>
              <h3 className={estilos.subtituloInsumos}>
                <IconoCaja /> Materiales y Consumibles ({materiales.length})
              </h3>
              {materiales.length === 0 ? (
                <p style={{ fontSize: '0.85rem', color: '#cfbdf7', margin: 0 }}>No hay materiales asignados.</p>
              ) : (
                <table className={estilos.tablaInsumosObra}>
                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Descripción</th>
                      <th style={{ textAlign: 'right' }}>Cant.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {materiales.map((m: any, idx: number) => (
                      <tr key={idx}>
                        <td>
                          <span className={estilos.codigoInsumo}>{m.codigo || '-'}</span>
                        </td>
                        <td>{m.nombre || m.descripcion}</td>
                        <td style={{ textAlign: 'right', fontWeight: 800, color: '#ffc43f' }}>
                          {m.cantidad} {m.unidad || 'und'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Columna Herramientas */}
            <div className={estilos.columnaInsumos}>
              <h3 className={estilos.subtituloInsumos}>
                <IconoHerramienta /> Herramientas y Equipos ({herramientas.length})
              </h3>
              {herramientas.length === 0 ? (
                <p style={{ fontSize: '0.85rem', color: '#cfbdf7', margin: 0 }}>No hay herramientas asignadas.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {herramientas.map((h: any, idx: number) => (
                    <div key={idx} className={estilos.tarjetaHerramientaObra}>
                      <div>
                        <div style={{ fontWeight: 700, color: '#ffffff', fontSize: '13px' }}>
                          {h.nombre || h.descripcion}
                        </div>
                        {h.codigo && (
                          <span className={estilos.codigoInsumo} style={{ fontSize: '10px' }}>
                            {h.codigo}
                          </span>
                        )}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <span style={{ fontWeight: 800, color: '#ffc43f', fontSize: '13px' }}>
                          {h.cantidad} {h.unidad || 'und'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </section>

      {/* Modal Flotante Selector de Cuadrilla (SÓLIDO, CON BOTONES CLAROS Y SIN RELOAD) */}
      {modalCuadrillaAbierto && (
        <div className={estilos.modalVelo} onClick={() => setModalCuadrillaAbierto(false)}>
          <div className={estilos.modalCaja} onClick={(e) => e.stopPropagation()}>
            <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1.5px solid #5C2CA8', paddingBottom: '10px' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: '#ffffff' }}>
                Modificar Cuadrilla del Módulo
              </h3>
              <button
                type="button"
                onClick={() => setModalCuadrillaAbierto(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: '#ffc43f' }}
              >
                ✕
              </button>
            </header>

            <div className={estilos.listaCheckboxesCuadrilla}>
              {cargandoOpciones ? (
                <p style={{ color: '#e5d8ff', fontSize: '0.85rem', padding: '8px' }}>Cargando colaboradores...</p>
              ) : opcionesColab.length === 0 ? (
                <p style={{ color: '#e5d8ff', fontSize: '0.85rem', padding: '8px' }}>Sin colaboradores disponibles</p>
              ) : (
                opcionesColab.map((op) => {
                  const seleccionado = cuadrillaTemp.includes(op.valor)
                  return (
                    <label key={op.valor} className={estilos.itemCheckCuadrilla}>
                      <input
                        type="checkbox"
                        checked={seleccionado}
                        onChange={() => alternarColabTemp(op.valor)}
                      />
                      <span>{op.etiqueta}</span>
                    </label>
                  )
                })
              )}
            </div>

            <footer style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', borderTop: '1.5px solid #5C2CA8', paddingTop: '12px' }}>
              <button
                type="button"
                onClick={() => setModalCuadrillaAbierto(false)}
                style={{
                  background: 'transparent',
                  color: '#e5d8ff',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '6px',
                  padding: '6px 14px',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={guardandoCuadrilla}
                onClick={handleConfirmarCuadrilla}
                style={{
                  background: '#ffc43f',
                  color: '#1a0033',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '6px 16px',
                  fontWeight: 800,
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                }}
              >
                {guardandoCuadrilla ? 'Guardando...' : 'Confirmar Asignación'}
              </button>
            </footer>
          </div>
        </div>
      )}

            {/* Modal de Ubicación GPS */}
      {modalMapaAbierto && (
        <ModalMapaUbicacionProyecto
          abierto={modalMapaAbierto}
          proyecto={{
            ...(proyecto || {}),
            id: proyecto?.id || orden.proyecto_plan_id || '',
            codigo: codigoProyecto,
            nombre: nombreProyecto,
            ubicacion: direccionObra,
            latitud: lat,
            longitud: lng,
            radio_geocerca_m: geocelda,
          } as any}
          onCerrar={() => setModalMapaAbierto(false)}
          onGuardar={async (nuevoLat, nuevoLng, nuevoRadio, nuevaDir) => {
            const pryId = proyecto?.id || orden.proyecto_plan_id
            try {
              if (pryId) {
                const cambios: any = {
                  latitud: nuevoLat,
                  longitud: nuevoLng,
                  radio_geocerca_m: nuevoRadio,
                }
                if (nuevaDir) cambios.ubicacion = nuevaDir
                await (supabase as any).from('proyectos_plan').update(cambios).eq('id', pryId)
              }
              await (supabase as any)
                .from('ordenes_servicio')
                .update({
                  latitud: nuevoLat,
                  longitud: nuevoLng,
                  radio_geocerca_m: nuevoRadio,
                  direccion: nuevaDir || direccionObra,
                })
                .eq('id', orden.id)

              setCoordsLocales({
                lat: nuevoLat,
                lng: nuevoLng,
                radio: nuevoRadio,
                dir: nuevaDir || direccionObra,
              })
              mostrarAviso('Ubicación guardada correctamente')
              setModalMapaAbierto(false)
            } catch (e) {
              console.error('Error guardando ubicacion:', e)
              mostrarAviso('Error al guardar ubicación')
            }
          }}
        />
      )}

      {/* Toast Notificación */}
      {toastMensaje && (
        <div
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            background: '#3A196E',
            color: '#ffc43f',
            border: '1.5px solid #ffc43f',
            padding: '10px 18px',
            borderRadius: '8px',
            fontSize: '0.85rem',
            fontWeight: 800,
            boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
            zIndex: 9999,
          }}
        >
          {toastMensaje}
        </div>
      )}
    </div>
  )
}
